import { useEffect, useState, createContext, useContext, useMemo } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import HomePage from "@/pages/HomePage";
import ProductsPage from "@/pages/ProductsPage";
import CheckoutPage from "@/pages/CheckoutPage";
import OrderConfirmationPage from "@/pages/OrderConfirmationPage";
import WishlistPage from "@/pages/WishlistPage";
import AdminDashboard from "@/pages/AdminDashboard";
import TermsPage from "@/pages/TermsPage";
import RefundsPage from "@/pages/RefundsPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import FaqsPage from "@/pages/FaqsPage";
import CookieConsent from "@/components/CookieConsent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Cart Context
export const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

// Variant base-name regex helpers (mirrors backend /api/products/{id}/variants logic).
// Strips trailing size, pack-count, and color codes so size siblings collapse to the same key.
const SIZE_RE = /\s+\d+(?:[.,]\d+)?\s*(?:kg|g|ml|l|oz|fl\s*oz)\s*$/i;
const PACK_RE = /\s*(?:\(?\s*pack\s+of\s+\d+\s*\)?|\d+[\s-]?pack)\s*$/i;
const COLOR_RE = /\s+colou?r\s+[A-Za-z0-9/]+\s*$/i;

const baseName = (raw) => {
  let n = (raw || "").trim();
  for (let i = 0; i < 3; i++) {
    const next = n.replace(SIZE_RE, "").replace(PACK_RE, "").replace(COLOR_RE, "").trim();
    if (next === n) break;
    n = next;
  }
  return n.toLowerCase();
};

// Format a unit price label given the parsed size and absolute price.
// Weight: shows £/kg if size ≥ 250g, else £/100g.
// Volume: shows £/L if size ≥ 250ml, else £/100ml.
const formatUnitPrice = (price, size) => {
  if (!size || size.value <= 0 || price <= 0) return null;
  if (size.kind === "weight") {
    if (size.value >= 250) {
      const perKg = (price / size.value) * 1000;
      return `£${perKg.toFixed(2)} / kg`;
    }
    const per100g = (price / size.value) * 100;
    return `£${per100g.toFixed(2)} / 100g`;
  }
  if (size.kind === "volume") {
    if (size.value >= 250) {
      const perL = (price / size.value) * 1000;
      return `£${perL.toFixed(2)} / L`;
    }
    const per100ml = (price / size.value) * 100;
    return `£${per100ml.toFixed(2)} / 100ml`;
  }
  return null;
};

// Extract the trailing size from a product name and normalize to a base unit:
//   weight  -> grams      (kg => *1000)
//   volume  -> millilitres (l => *1000)
//   oz      -> grams       (1 oz ≈ 28.3495 g)
// Returns { value: number, kind: 'weight'|'volume' } or null if no size suffix found.
const SIZE_PARSE_RE = /(\d+(?:[.,]\d+)?)\s*(kg|g|ml|l|oz|fl\s*oz)\s*$/i;
const parseSize = (name) => {
  const m = (name || "").trim().match(SIZE_PARSE_RE);
  if (!m) return null;
  const num = parseFloat(m[1].replace(",", "."));
  if (!isFinite(num) || num <= 0) return null;
  const unit = m[2].toLowerCase().replace(/\s+/g, "");
  if (unit === "kg") return { value: num * 1000, kind: "weight" };
  if (unit === "g") return { value: num, kind: "weight" };
  if (unit === "oz") return { value: num * 28.3495, kind: "weight" };
  if (unit === "l") return { value: num * 1000, kind: "volume" };
  if (unit === "ml") return { value: num, kind: "volume" };
  if (unit === "floz") return { value: num * 29.5735, kind: "volume" };
  return null;
};

function App() {
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem("forton-cart");
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  // Save cart to localStorage
  useEffect(() => {
    localStorage.setItem("forton-cart", JSON.stringify(cart));
  }, [cart]);

  // Fetch storefront data on mount (categories, products, editable settings)
  useEffect(() => {
    const initializeData = async () => {
      try {
        const [catRes, prodRes, settingsRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/products`),
          axios.get(`${API}/settings`),
        ]);

        setCategories(catRes.data);
        setProducts(prodRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error("[App] Failed to initialize app data:", error);
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  // Cart functions
  const addToCart = (product, quantity = 1, weightOption = null) => {
    setCart((prev) => {
      const existingIndex = prev.findIndex(
        (item) => item.product_id === product.id && item.weight_option === weightOption
      );

      if (existingIndex > -1) {
        const updated = [...prev];
        updated[existingIndex].quantity += quantity;
        return updated;
      }

      return [
        ...prev,
        {
          product_id: product.id,
          product_name: product.name,
          quantity,
          price: product.is_special && product.special_price ? product.special_price : product.price,
          unit: product.unit,
          weight_option: weightOption,
          image_url: product.image_url,
        },
      ];
    });
  };

  const removeFromCart = (productId, weightOption = null) => {
    setCart((prev) =>
      prev.filter(
        (item) => !(item.product_id === productId && item.weight_option === weightOption)
      )
    );
  };

  const updateQuantity = (productId, quantity, weightOption = null) => {
    if (quantity <= 0) {
      removeFromCart(productId, weightOption);
      return;
    }

    setCart((prev) =>
      prev.map((item) =>
        item.product_id === productId && item.weight_option === weightOption
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Compute (a) variant counts, (b) best-value savings, and (c) per-unit price label
  // for every product that has a parseable size suffix.
  const { variantCounts, variantSavings, unitPrices } = useMemo(() => {
    const groups = {};
    const unitPriceMap = {};
    for (const p of products) {
      if (!p.is_available) continue;
      const key = `${p.category_id}|${baseName(p.name)}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
      // Per-unit price label (£/kg, £/100g, £/L, £/100ml) for any product with a size suffix
      const price = p.is_special && p.special_price ? p.special_price : p.price;
      const size = parseSize(p.name);
      const label = formatUnitPrice(price, size);
      if (label) unitPriceMap[p.id] = label;
    }
    const counts = {};
    const savings = {};
    for (const key in groups) {
      const members = groups[key];
      if (members.length < 2) continue;
      // 1) variant counts
      for (const m of members) counts[m.id] = members.length - 1;
      // 2) best-value savings — only when all members share the same size kind.
      const parsed = members
        .map((m) => {
          const price = m.is_special && m.special_price ? m.special_price : m.price;
          const size = parseSize(m.name);
          return size && price > 0 ? { id: m.id, price, size } : null;
        })
        .filter(Boolean);
      if (parsed.length < 2) continue;
      const kinds = new Set(parsed.map((x) => x.size.kind));
      if (kinds.size !== 1) continue;
      const withPU = parsed.map((x) => ({ ...x, pu: x.price / x.size.value }));
      withPU.sort((a, b) => a.pu - b.pu);
      const cheapest = withPU[0];
      const dearest = withPU[withPU.length - 1];
      if (cheapest.id === dearest.id) continue;
      const savePct = Math.round((1 - cheapest.pu / dearest.pu) * 100);
      if (savePct >= 5) savings[cheapest.id] = savePct;
    }
    return { variantCounts: counts, variantSavings: savings, unitPrices: unitPriceMap };
  }, [products]);

  const cartContextValue = {
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    cartTotal,
    cartCount,
    categories,
    products,
    variantCounts,
    variantSavings,
    unitPrices,
    settings,
    loading,
  };

  return (
    <CartContext.Provider value={cartContextValue}>
      <div className="App min-h-screen bg-white">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/products/:categorySlug" element={<ProductsPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/order-confirmation" element={<OrderConfirmationPage />} />
            <Route path="/wishlist" element={<WishlistPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/refunds" element={<RefundsPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/faqs" element={<FaqsPage />} />
            <Route path="/admin" element={<AdminDashboard />} />
          </Routes>
          <CookieConsent />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </CartContext.Provider>
  );
}

export default App;
